// =============================
//  FULL UPDATED AdminVoting.jsx
// =============================
import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./ElectionPeriod.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");

const getToken = () => localStorage.getItem("token") || "";

function formatLocal(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Invalid Date";

    return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: "America/Edmonton",
    });
}

function turnoutPct(voted, total) {
    if (!total || total === 0) return 0;
    return (voted / total) * 100;
}
function voteSharePct(votes, totalVotes) {
    if (!totalVotes || totalVotes === 0) return 0;
    return (votes / totalVotes) * 100;
}

function turnoutClass(pct) {
    if (pct >= 50) return "turnout-high";
    if (pct < 10) return "turnout-low";
    return "turnout-mid";
}

function candidateTurnoutPct(votes, totalVoted) {
    if (!totalVoted || totalVoted === 0) return 0;
    return (votes / totalVoted) * 100;
}

function maskHash(hash) {
    if (!hash) return "—";
    return hash.slice(0, 8) + "…";
}

function buildIPUsageMap(voters) {
    const map = {};
    voters.forEach(v => {
        if (!v.ip_hash) return;
        map[v.ip_hash] = (map[v.ip_hash] || 0) + 1;
    });
    return map;
}

function isDuplicateIP(hash, map) {
    if (!hash) return false;
    return map[hash] > 1;
}


export default function AdminVoting({ role = "admin", mode = "active" }) {
    const isFinishedMode = mode === "finished";
    // --------------------------------
    // PASSWORD SESSION CONTROL (NEW)
    // --------------------------------
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authPassword, setAuthPassword] = useState("");
    const [authError, setAuthError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const wantsOpen = sessionStorage.getItem("openOVS");
        sessionStorage.removeItem("openOVS");

        const hasSession = localStorage.getItem("soal_admin_ovs_session");

        if (role === "comelec") {
            localStorage.setItem("soal_admin_ovs_session", "active");
            setAuthModalOpen(false);
            return;
        }

        if (wantsOpen === "yes" && !hasSession) {
            setAuthModalOpen(true);
        }
    }, [role]);

    // --------------------------------
    // ELECTION STATES
    // --------------------------------
    const [elections, setElections] = useState([]);
    const [title, setTitle] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedElection, setSelectedElection] = useState("");
    const [selectedElectionData, setSelectedElectionData] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [newCandidate, setNewCandidate] = useState("");
    const [results, setResults] = useState([]);
    const [voters, setVoters] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [participation, setParticipation] = useState(null);


    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [toastMessage, setToastMessage] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [candidateToDelete, setCandidateToDelete] = useState(null);
    const [confirmElectionOpen, setConfirmElectionOpen] = useState(false);
    const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
    const [confirmMoveOpen, setConfirmMoveOpen] = useState(false);

    const [auditOpen, setAuditOpen] = useState(false);
    const [auditTab, setAuditTab] = useState("summary");
    const ipStats = buildIPUsageMap(voters);
    const ipUsageMap = ipStats;

    const totalVotes = voters.length;
    const uniqueIPs = Object.keys(ipStats).length;
    const flaggedIPs = Object.values(ipStats).filter(c => c >= 3).length;
    const [updatingVisibility, setUpdatingVisibility] = useState(false);


    const maxVotesFromIP =
        Object.values(ipStats).length > 0
            ? Math.max(...Object.values(ipStats))
            : 0;

    const totalResultVotes = results.reduce(
        (sum, r) => sum + Number(r.votes || 0),
        0
    );

    const visibleElections = useMemo(
        () =>
            elections.filter((e) =>
                isFinishedMode
                    ? !!e.moved_to_previous
                    : !e.moved_to_previous
            ),
        [elections, isFinishedMode]
    );

    function fraudRiskLevel() {
        if (flaggedIPs >= 3 || maxVotesFromIP >= 5) return "HIGH";
        if (flaggedIPs >= 1) return "MEDIUM";
        return "LOW";
    }

    function exportAuditCSV(ipStats) {
        const header = ["IP Hash", "Vote Count", "Flagged"];

        const rows = Object.entries(ipStats).map(([hash, count]) => [
            hash,
            count,
            count >= 3 ? "YES" : "NO",
        ]);

        const csv =
            "data:text/csv;charset=utf-8," +
            [header, ...rows].map(r => r.join(",")).join("\n");

        const link = document.createElement("a");
        link.href = encodeURI(csv);
        link.download = "election_audit_report.csv";
        link.click();
    }

    const isVotingStarted =
        selectedElectionData &&
        new Date() >= new Date(selectedElectionData.start_time);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(""), 1000);
    };

    // --------------------------------
    // LOAD ELECTIONS
    // --------------------------------
    const loadElections = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/voting/election`);
            const data = await res.json();
            if (data.ok) setElections(data.rows);
        } catch (err) {
            console.error("Failed to load elections:", err);
        }
    }, []);

    useEffect(() => {
        loadElections();
    }, [loadElections]);

    useEffect(() => {
        if (!selectedElection) return;
        const stillVisible = visibleElections.some((e) => e.id === Number(selectedElection));
        if (!stillVisible) {
            setSelectedElection("");
            setSelectedElectionData(null);
            setCandidates([]);
            setVoters([]);
            setResults([]);
            setParticipation(null);
        }
    }, [selectedElection, visibleElections]);

    // --------------------------------
    // COUNT VOTES
    // --------------------------------
    const countVotes = useCallback(async (id) => {
        try {
            const res = await fetch(`${API_BASE}/api/voting/results/${id}`);
            const data = await res.json();
            if (data.ok) {
                setResults(data.rows.sort((a, b) => b.votes - a.votes));
            }
        } catch (err) {
            console.error("Error loading results:", err);
        }
    }, []);

    const checkIfEnded = useCallback(
        async (id) => {
            const e = elections.find((x) => x.id === Number(id));
            if (!e) return;
            if (new Date() > new Date(e.end_time)) await countVotes(id);
        },
        [elections, countVotes]
    );

    // --------------------------------
    // SELECT ELECTION
    // --------------------------------
    const handleSelectElection = useCallback(
        async (id) => {
            setSelectedElection(id);
            if (!id) return;

            try {
                const res = await fetch(`${API_BASE}/api/voting/election`);
                const list = await res.json();

                if (list.ok) {
                    const fresh = list.rows.find(x => x.id === Number(id));
                    setSelectedElectionData(fresh || null);
                }

                const [
                    candidatesRes,
                    votersRes,
                    participationRes
                ] = await Promise.all([
                    fetch(`${API_BASE}/api/voting/results/${id}`),
                    fetch(`${API_BASE}/api/voting/voters/${id}`, {
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }),
                    fetch(`${API_BASE}/api/voting/participation/${id}`)
                ]);

                const candData = await candidatesRes.json();
                const voteData = await votersRes.json();
                const partData = await participationRes.json();

                setCandidates(candData.ok ? candData.rows : []);
                setVoters(voteData.ok ? voteData.rows : []);
                setParticipation(partData.ok ? partData.data : null);

                await checkIfEnded(id);

            } catch (err) {
                console.error("❌ Failed to load election data:", err);
            }
        },
        [checkIfEnded]
    );

    // Auto reload every 10 seconds
    useEffect(() => {
        const interval = setInterval(async () => {
            await loadElections();
            if (selectedElection) await handleSelectElection(selectedElection);
        }, 10000);

        return () => clearInterval(interval);
    }, [selectedElection, loadElections, handleSelectElection]);

    // --------------------------------
    // CREATE ELECTION (CONFIRMED)
    // --------------------------------
    const handleCreateElection = async () => {
        if (!title || !startDate || !endDate) {
            showToast("Please fill in all fields.");
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/voting/election`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({
                    title,
                    start_time: startDate,
                    end_time: endDate,
                }),
            });

            const data = await res.json();

            if (data.ok) {
                showToast("Election created.");

                setTitle("");
                setStartDate("");
                setEndDate("");
                loadElections();
            } else {
                showToast(data.error || "Failed to create election.");
            }
        } catch {
            showToast("Network error while creating election.");
        } finally {
            setConfirmCreateOpen(false);
        }
    }

    // --------------------------------
    // ADD CANDIDATE (CENTER TOAST)
    // --------------------------------
    const handleAddCandidate = async () => {
        if (!selectedElection) {
            showToast("Select an election first.");
            return;
        }

        if (!newCandidate.trim()) {
            showToast("Enter candidate name.");
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/voting/candidate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({
                    election_id: selectedElection,
                    name: newCandidate.trim(),
                }),
            });

            const data = await res.json();

            if (data.ok) {
                showToast("Candidate added.");
                setNewCandidate("");
                await handleSelectElection(selectedElection);
            } else {
                showToast(data.error || "Failed to add candidate.");
            }
        } catch {
            showToast("Error adding candidate.");
        }
    };


    // --------------------------------
    // DELETE ELECTION
    // --------------------------------
    const handleDeleteElection = async () => {
        if (!selectedElection) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/voting/election/${selectedElection}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${getToken()}` },
                }
            );

            const data = await res.json();

            if (data.ok) {
                showToast("Election deleted.");

                setSelectedElection(null);
                setSelectedElectionData(null);
                setCandidates([]);
                setVoters([]);
                setResults([]);

                loadElections();
            } else {
                showToast(data.error || "Delete failed.");
            }
        } catch {
            showToast("Network error while deleting election.");
        } finally {
            setConfirmElectionOpen(false);
        }
    };

    const handleMoveToPreviousElection = async () => {
        if (!selectedElectionData) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/voting/election/${selectedElectionData.id}/previous`,
                {
                    method: "PATCH",
                    headers: { Authorization: `Bearer ${getToken()}` },
                }
            );
            const data = await res.json();

            if (!res.ok || !data.ok) {
                showToast(data?.error || "Failed to move election.");
                return;
            }

            showToast("Election moved to Previous Election.");
            setSelectedElection("");
            setSelectedElectionData(null);
            setCandidates([]);
            setVoters([]);
            setResults([]);
            setParticipation(null);
            await loadElections();
        } catch {
            showToast("Network error while moving election.");
        } finally {
            setConfirmMoveOpen(false);
        }
    };

    // --------------------------------
    // TOGGLE UNOFFICIAL RESULTS VISIBILITY
    // --------------------------------
    const toggleResultsVisibility = async () => {
        if (!selectedElectionData || updatingVisibility) return;

        const newValue = !selectedElectionData.show_unofficial_results;

        setUpdatingVisibility(true); // 🔒 lock toggle

        try {
            const res = await fetch(
                `${API_BASE}/api/voting/election/${selectedElectionData.id}/results-visibility`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify({ show: newValue }),
                }
            );

            const data = await res.json();
            if (!data.ok) throw new Error(data.error);

            // ✅ instant UI update
            setSelectedElectionData(prev => ({
                ...prev,
                show_unofficial_results: newValue,
            }));

            showToast(
                newValue
                    ? "Unofficial results are now VISIBLE to voters."
                    : "Unofficial results are now HIDDEN from voters."
            );
        } catch (err) {
            console.error("Toggle visibility error:", err);
            showToast("Failed to update visibility.");
        } finally {
            setUpdatingVisibility(false); // 🔓 unlock toggle
        }
    };


    // --------------------------------
    // DELETE CANDIDATE (SINGLE CONFIRM + TOAST)
    // --------------------------------
    const handleDeleteCandidate = async () => {
        if (!candidateToDelete) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/voting/candidate/${candidateToDelete}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${getToken()}` },
                }
            );

            const data = await res.json();

            if (data.ok) {
                showToast("Candidate deleted.");
                await handleSelectElection(selectedElection);
            } else {
                showToast(data.error || "Delete failed.");
            }
        } catch {
            showToast("Network error while deleting candidate.");
        } finally {
            setConfirmOpen(false);
            setCandidateToDelete(null);
        }
    };


    // --------------------------------
    // STATUS COLOR BADGES
    // --------------------------------
    const getStatusColor = (status) => {
        if (!status) return "";
        switch (status.toLowerCase()) {
            case "upcoming":
                return "status-upcoming";
            case "ongoing":
                return "status-ongoing";
            case "closed":
                return "status-closed";
            default:
                return "";
        }
    };


    // --------------------------------
    // FILTER VOTERS LIST
    // --------------------------------
    const filteredVoters = voters.filter((v) =>
        `${v.primary_id} ${v.first_name} ${v.last_name} ${v.email}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );

    // --------------------------------
    // EXPORT CSV
    // --------------------------------
    const exportCSV = () => {
        if (!voters.length) {
            alert("No voter data to export.");
            return;
        }

        const header = [
            "Primary ID",
            "First Name",
            "Last Name",
            "Email",
            "Mobile",
            "IP Address Hash",
            "Code",
            "Candidate",
            "Timestamp",
        ];

        const rows = voters.map((v) => [
            v.primary_id,
            v.first_name,
            v.last_name,
            v.email,
            v.mobile || "",
            maskHash(v.ip_hash),
            v.code,
            v.candidate_name,
            formatLocal(v.created_at),
        ]);

        const csv =
            "data:text/csv;charset=utf-8," +
            [header, ...rows].map((r) => r.join(",")).join("\n");

        const link = document.createElement("a");
        link.href = encodeURI(csv);
        link.download = "voters_export.csv";
        link.click();
    };


    // --------------------------------
    // TRY UNLOCK PASSWORD Comelec
    // --------------------------------
    const tryUnlock = async () => {
        setAuthError("");

        try {
            const res = await fetch(`${API_BASE}/api/admin/unlock`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ password: authPassword }),
            });

            const data = await res.json();

            if (data.ok) {
                localStorage.setItem("soal_admin_ovs_session", "active");
                setAuthModalOpen(false);
            } else {
                setAuthError("Password is incorrect.");
            }
        } catch {
            setAuthError("Server error. Try again.");
        }
    };

    // -----------------------------------------------
    // AUTH MODAL (SHOWN BEFORE ENTERING OVS)
    // -----------------------------------------------
    if (authModalOpen) {

        const handleCancelAuth = () => {
            setAuthModalOpen(false);

            sessionStorage.removeItem("openOVS");
            localStorage.removeItem("soal_admin_ovs_session");

            sessionStorage.setItem("forceProfile", "yes");
            window.dispatchEvent(new Event("force-profile"));
        };



        return (

            <div className="auth-modal-overlay">
                <div className="auth-modal">
                    <h2>Welcome to SARC - COMELEC</h2>
                    <p className="subtitle">Automated Online Voting System</p>

                    <label className="auth-label">Enter Password</label>

                    <div className="auth-input-wrapper">
                        <input
                            type={showPassword ? "text" : "password"}
                            value={authPassword}
                            onChange={(e) => setAuthPassword(e.target.value)}
                            className="auth-input"
                            placeholder="Enter Password"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") tryUnlock();
                            }}
                        />
                        <span
                            className="toggle-eye"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? "👁️" : "👁️‍🗨️"}
                        </span>
                    </div>

                    {authError && <p className="auth-error">{authError}</p>}

                    <div className="auth-buttons">
                        <button className="auth-btn" onClick={tryUnlock}>
                            Unlock OVS
                        </button>

                        <button className="auth-btn cancel-btn" onClick={handleCancelAuth}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // -----------------------------------------------
    // ADMIN PANEL AFTER SUCCESSFUL LOGIN
    // -----------------------------------------------
    return (
        <div className="admin-voting-container">

            {/* 🔔 TOAST MESSAGE */}
            {toastMessage && (
                <div className="toast-message">
                    {toastMessage}
                </div>
            )}

            {/* ❗ CONFIRM DELETE CANDIDATE MODAL */}
            {confirmOpen && (
                <div className="confirm-overlay">
                    <div className="confirm-modal">
                        <h3>Confirm Deletion</h3>
                        <p>
                            Are you sure you want to delete this candidate?
                            <br />
                            <span className="warning-text">
                                This action cannot be undone.
                            </span>
                        </p>

                        <div className="confirm-actions">
                            <button
                                className="confirm-btn danger"
                                onClick={handleDeleteCandidate}
                            >
                                Delete
                            </button>

                            <button
                                className="confirm-btn cancel"
                                onClick={() => {
                                    setConfirmOpen(false);
                                    setCandidateToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ❗ CONFIRM DELETE ELECTION MODAL (ADD THIS BELOW) */}
            {confirmElectionOpen && (
                <div className="confirm-overlay">
                    <div className="confirm-modal">
                        <h3>Confirm Deletion</h3>
                        <p>
                            Delete this election permanently?
                            <br />
                            <span className="warning-text">
                                This action cannot be undone.
                            </span>
                        </p>

                        <div className="confirm-actions">
                            <button
                                className="confirm-btn danger"
                                onClick={handleDeleteElection}
                            >
                                Delete
                            </button>

                            <button
                                className="confirm-btn cancel"
                                onClick={() => setConfirmElectionOpen(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmMoveOpen && (
                <div className="confirm-overlay">
                    <div className="confirm-modal">
                        <h3>Move Election</h3>
                        <p>
                            Do you want to move this election to Previous Election?
                            <br />
                            <span className="warning-text">
                                It will no longer appear in the main OVS election list.
                            </span>
                        </p>

                        <div className="confirm-actions">
                            <button
                                className="confirm-btn"
                                onClick={handleMoveToPreviousElection}
                            >
                                Yes
                            </button>

                            <button
                                className="confirm-btn cancel"
                                onClick={() => setConfirmMoveOpen(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {auditOpen && (
                <div className="confirm-overlay">
                    <div className="confirm-modal audit-modal">
                        <h3>🧾 Election Audit Report</h3>

                        {/* 🔖 TABS */}
                        <div className="audit-tabs">
                            <button
                                className={auditTab === "summary" ? "active" : ""}
                                onClick={() => setAuditTab("summary")}
                            >
                                📊 Summary
                            </button>

                            <button
                                className={auditTab === "details" ? "active" : ""}
                                onClick={() => setAuditTab("details")}
                            >
                                🧾 Details
                            </button>
                        </div>

                        {/* ================= SUMMARY PANEL ================= */}
                        {auditTab === "summary" && (
                            <div className="audit-summary">
                                <div className="summary-card">
                                    <span>Total Votes</span>
                                    <strong>{totalVotes}</strong>
                                </div>

                                <div className="summary-card">
                                    <span>Unique IPs</span>
                                    <strong>{uniqueIPs}</strong>
                                </div>

                                <div className="summary-card danger">
                                    <span>Flagged IPs (3+)</span>
                                    <strong>{flaggedIPs}</strong>
                                </div>

                                <div className="summary-card">
                                    <span>Max Votes / IP</span>
                                    <strong>{maxVotesFromIP}</strong>
                                </div>

                                <div className={`summary-risk risk-${fraudRiskLevel().toLowerCase()}`}>
                                    Fraud Risk Level: <b>{fraudRiskLevel()}</b>
                                </div>
                            </div>
                        )}

                        {/* ================= DETAILS TABLE ================= */}
                        {auditTab === "details" && (
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>IP Hash</th>
                                        <th>Votes</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(ipStats).map(([hash, count]) => (
                                        <tr
                                            key={hash}
                                            className={count >= 3 ? "audit-danger" : ""}
                                        >
                                            <td>{maskHash(hash)}</td>
                                            <td>{count}</td>
                                            <td>{count >= 3 ? "⚠️ Flagged" : "OK"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* ACTIONS */}
                        <div className="confirm-actions">
                            <button
                                className="confirm-btn danger"
                                onClick={() => exportAuditCSV(ipStats)}
                                disabled={auditTab !== "details"}
                                title={
                                    auditTab !== "details"
                                        ? "Switch to Details tab to export audit report"
                                        : "Export audit report"
                                }
                            >
                                📤 Export CSV
                            </button>


                            <button
                                className="confirm-btn cancel"
                                onClick={() => {
                                    setAuditTab("summary");
                                    setAuditOpen(false);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* ❗ CONFIRM CREATE ELECTION MODAL */}
            {confirmCreateOpen && (
                <div className="confirm-overlay">
                    <div className="confirm-modal">
                        <h3>Confirm Creation</h3>
                        <p>
                            Create this election with the provided details?
                        </p>

                        <div className="confirm-actions">
                            <button
                                className="confirm-btn danger"
                                onClick={handleCreateElection}
                            >
                                OK
                            </button>

                            <button
                                className="confirm-btn cancel"
                                onClick={() => setConfirmCreateOpen(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* ----------------- CREATE ELECTION ----------------- */}
            {!isFinishedMode && (
            <div className="election-form">

                <h2>Admin Voting Panel</h2>
                <div className="floating-wrapper">
                    <label className="floating-label">Tittle:</label>
                <input
                    type="text"
                    placeholder="Election Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    />
                </div>

                <div className="floating-wrapper">
                    <label className="floating-label">Start Date:</label>
                    <input
                        type="datetime-local"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>

                <div className="floating-wrapper">
                    <label className="floating-label">End Date:</label>
                    <input
                        type="datetime-local"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>

                <button onClick={() => setConfirmCreateOpen(true)}>
                    Create Election
                </button>

            </div>
            )}

            {/* ----------------- SELECT ELECTION ----------------- */}
            <div className="election-dropdown">
                <h3>{isFinishedMode ? "Choose Previous Election" : "Choose Election"}</h3>
                <select
                    value={selectedElection || ""}
                    onChange={(e) => handleSelectElection(e.target.value)}
                >
                    <option value="">
                        {isFinishedMode ? "Select Previous Election" : "Select Election"}
                    </option>
                    {visibleElections.map((e) => (
                        <option key={e.id} value={e.id}>
                            {e.title}
                        </option>
                    ))}
                </select>
                {visibleElections.length === 0 && (
                    <p className="election-empty-note">
                        {isFinishedMode
                            ? "No previous elections yet."
                            : "No new, incoming, ongoing, or unsaved completed elections available."}
                    </p>
                )}
            </div>

            {/* ----------------- ELECTION DETAILS ----------------- */}
            {selectedElectionData && (
                <div className="election-details">
                    <h4>Election Details</h4>

                    <p><b>Title:</b> {selectedElectionData.title}</p>
                    <p><b>Start:</b> {formatLocal(selectedElectionData.start_time)}</p>
                    <p><b>End:</b> {formatLocal(selectedElectionData.end_time)}</p>

                    <p>
                        <b>Status:</b>{" "}
                        <span
                            className={`status-tag ${getStatusColor(selectedElectionData.status)}`}
                        >
                            {selectedElectionData.status.toUpperCase()}
                        </span>
                    </p>

                    {/* ✅ ADMIN RESULTS TOGGLE */}
                    {selectedElectionData.status === "closed" && (
                        <div className="results-toggle">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!selectedElectionData.show_unofficial_results}
                                    onChange={toggleResultsVisibility}
                                    disabled={updatingVisibility}
                                />
                                <span className="slider" />
                            </label>

                            <span className="toggle-label">
                                {selectedElectionData.show_unofficial_results
                                    ? "Unofficial results are VISIBLE to voters"
                                    : "Unofficial results are HIDDEN from voters"}
                            </span>
                        </div>
                    )}

                    <button
                        onClick={() => setConfirmElectionOpen(true)}
                        className="delete-btn"
                    >
                        Delete
                    </button>

                    {!isFinishedMode &&
                        selectedElectionData.status === "closed" &&
                        !selectedElectionData.moved_to_previous && (
                            <button
                                onClick={() => setConfirmMoveOpen(true)}
                                className="save-election-btn"
                            >
                                Save
                            </button>
                        )}
                </div>
            )}


            {/* ----------------- CANDIDATES ----------------- */}
            {selectedElection && (
                <div className="candidate-section">
                    <h3>Candidates ({candidates.length}/10)</h3>

                    <div className="add-candidate-box">
                        <input
                            type="text"
                            placeholder="Candidate Name"
                            value={newCandidate}
                            onChange={(e) => setNewCandidate(e.target.value)}
                            disabled={
                                !selectedElectionData ||
                                new Date() >= new Date(selectedElectionData.start_time)
                            }
                        />
                        <button
                            onClick={handleAddCandidate}
                            disabled={
                                !selectedElectionData ||
                                new Date() >= new Date(selectedElectionData.start_time)
                            }
                        >
                            Add
                        </button>
                    </div>

                    {participation?.PHI && participation?.SIGMA && (() => {
                        const totalVoted =
                            participation.PHI.voted + participation.SIGMA.voted;

                        const totalMembers =
                            participation.PHI.total + participation.SIGMA.total;

                        const overallPct = turnoutPct(totalVoted, totalMembers);

                        return (
                            <>
                                <div className="turnout-center">
                                    <div className={`overall-turnout-live ${turnoutClass(overallPct)}`}>
                                        📊 <b>Total Turnout:</b>{" "}
                                        {totalVoted}/{totalMembers} ({overallPct.toFixed(1)}%)
                                    </div>

                                    <div className="vote-progress-container">
                                        <div
                                            className="vote-progress-bar"
                                            style={{ width: `${overallPct}%` }}
                                        />
                                    </div>
                                </div>
                            </>
                        );
                    })()}

                    <div className="candidate-table">
                        <div className="table-header">
                            <span>Name</span>
                            <span className="center">Votes %</span>
                            <span className="center">Votes</span>
                            <span className="center">Action</span>
                        </div>

                        {candidates.map((c) => (
                            <div className="candidate-row" key={c.id}>
                                <span>{c.name}</span>

                                <span className={`center ${turnoutClass(
                                    candidateTurnoutPct(c.votes || 0, totalVotes)
                                )}`}>
                                    {candidateTurnoutPct(c.votes || 0, totalVotes).toFixed(1)}%
                                </span>

                                <span className="center">
                                    {c.votes || 0}
                                </span>

                                <span className="center">
                                    <button
                                        className="delete-candidate-btn"
                                        disabled={isVotingStarted}
                                        onClick={() => {
                                            setCandidateToDelete(c.id);
                                            setConfirmOpen(true);
                                        }}
                                    >
                                        Delete
                                    </button>
                                </span>
                            </div>
                        ))}
                    </div>

                </div>
            )}

            {/* ----------------- RESULTS (IF CLOSED) ----------------- */}
            {selectedElectionData &&
                new Date() > new Date(selectedElectionData.end_time) && (
                    <div className="results-section">
                        <h3>Results</h3>

                        {/* Participation pill */}
                        {participation?.PHI && participation?.SIGMA && (() => {
                            const phiPct = turnoutPct(
                                participation.PHI.voted,
                                participation.PHI.total
                            );

                            const sigmaPct = turnoutPct(
                                participation.SIGMA.voted,
                                participation.SIGMA.total
                            );

                            const totalVoted =
                                participation.PHI.voted + participation.SIGMA.voted;

                            const totalMembers =
                                participation.PHI.total + participation.SIGMA.total;

                            const overallPct = turnoutPct(totalVoted, totalMembers);

                            return (
                                <div className="participation-pill">
                                    <span className={turnoutClass(phiPct)}>
                                        <b>PHI</b>{" "}
                                        {participation.PHI.voted}/{participation.PHI.total}{" "}
                                        ({phiPct.toFixed(1)}%)
                                    </span>

                                    <span className="divider">&nbsp;|&nbsp;</span>

                                    <span className={turnoutClass(sigmaPct)}>
                                        <b>SIGMA</b>{" "}
                                        {participation.SIGMA.voted}/{participation.SIGMA.total}{" "}
                                        ({sigmaPct.toFixed(1)}%)
                                    </span>

                                    <span className="divider">&nbsp;|</span>

                                    <span className={`overall-turnout ${turnoutClass(overallPct)}`}>
                                        <b>TOTAL</b>{" "}
                                        {totalVoted}/{totalMembers} ({overallPct.toFixed(1)}%)
                                    </span>
                                </div>
                            );
                        })()}

                        {/* Scroll wrapper */}
                        <div className="results-wrapper">
                            <div className="results-table">
                                <div className="results-header">
                                    <div className="col-rank">Rank</div>
                                    <div className="col-name">Candidate</div>
                                    <div className="col-pct">Vote %</div>
                                    <div className="col-votes">Votes</div>
                                </div>

                                {/* EMPTY STATE */}
                                {results.length === 0 && (
                                    <div className="result-row">
                                        <div
                                            className="col-name"
                                            style={{
                                                width: "100%",
                                                textAlign: "center",
                                                color: "#aaa",
                                                fontStyle: "italic",
                                                padding: "12px 0",
                                            }}
                                        >
                                            No votes recorded yet
                                        </div>
                                    </div>
                                )}

                                {/* RESULTS ROWS */}
                                {results.map((r, i) => {
                                    const votePct = voteSharePct(
                                        r.votes || 0,
                                        totalResultVotes
                                    );

                                    return (
                                        <div
                                            key={r.id}
                                            className={`result-row ${i === 0 ? "winner" : ""}`}
                                        >
                                            <div className="col-rank">{i + 1}</div>
                                            <div className="col-name">{r.name}</div>
                                            <div className={`col-pct ${turnoutClass(votePct)}`}>
                                                {votePct.toFixed(1)}%
                                            </div>
                                            <div className="col-votes">{r.votes}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Mobile-only swipe hint */}
                        <p className="results-swipe-hint">
                            ↔ Swipe left and right for more details
                        </p>
                    </div>
                )}

            {/* ----------------- VOTERS TABLE WITH PAGINATION ----------------- */}
            {voters.length > 0 && (
                <div className="voters-section">
                    <h3>Voters Records</h3>

                    {/* Search + Export */}
                    <div className="voter-tools">
                        <input
                            type="text"
                            className="voter-search"
                            placeholder="Search name, email, ID…"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setCurrentPage(1);
                            }}
                        />

                        {/* ACTION BUTTONS */}
                        <div className="voter-actions">
                            <button
                                className="export-btn danger"
                                onClick={() => setAuditOpen(true)}
                            >
                                🧾 Audit Report
                            </button>

                            <button
                                className="export-btn"
                                onClick={exportCSV}
                            >
                                📤 Export CSV
                            </button>
                        </div>
                    </div>

                    {/* PAGINATION CALCULATION */}
                    {(() => {
                        const indexOfLast = currentPage * rowsPerPage;
                        const indexOfFirst = indexOfLast - rowsPerPage;
                        const currentRows = filteredVoters.slice(indexOfFirst, indexOfLast);
                        const totalPages = Math.ceil(filteredVoters.length / rowsPerPage);

                        const nextPage = () => {
                            if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                        };

                        const prevPage = () => {
                            if (currentPage > 1) setCurrentPage(currentPage - 1);
                        };

                        return (
                            <>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Code</th>
                                            <th>Candidate</th>
                                            <th>IP_Hash</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {currentRows.map((v) => (
                                            <tr
                                                key={v.id}
                                                className={
                                                    isDuplicateIP(v.ip_hash, ipUsageMap)
                                                        ? "ip-duplicate-row"
                                                        : ""
                                                }
                                            >
                                                <td>{v.primary_id}</td>
                                                <td>{v.first_name} {v.last_name}</td>
                                                <td>{v.email}</td>
                                                <td>{v.code}</td>
                                                <td>{v.candidate_name}</td>

                                                <td>
                                                    {maskHash(v.ip_hash)}
                                                    {isDuplicateIP(v.ip_hash, ipUsageMap) && (
                                                        <span className="ip-warning">
                                                            ⚠️ {ipUsageMap[v.ip_hash]} votes
                                                        </span>
                                                    )}
                                                </td>

                                                <td>{formatLocal(v.created_at)}</td>
                                            </tr>
                                        ))}

                                        {filteredVoters.length === 0 && (
                                            <tr>
                                                <td colSpan="7" style={{ textAlign: "center" }}>
                                                    No voters found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* PAGINATION CONTROLS */}
                                <div className="pagination-wrapper">

                                    <div className="rows-selector">
                                        <select
                                            value={rowsPerPage}
                                            onChange={(e) => {
                                                setRowsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value={10}>10 | Entries</option>
                                            <option value={25}>25 | Entries</option>
                                            <option value={50}>50 | Entries</option>
                                            <option value={100}>100 | Entries</option>
                                        </select>
                                    </div>

                                    <div className="pagination-controls">
                                        <button
                                            onClick={prevPage}
                                            disabled={currentPage === 1}
                                            className="pagination-btn"
                                        >
                                            Prev
                                        </button>

                                        <span className="pagination-info">
                                            Page {currentPage} of {totalPages || 1}
                                        </span>

                                        <button
                                            onClick={nextPage}
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}

